<script lang="ts">
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import type { ContactFormInput } from "../../../../core/schema/ContactForm.js";
	import sendContactForm from "../../api/mutations/sendContactForm.graphql";
	import LabelledInput from "../Form/LabelledInput.svelte";
	import GdprCheckbox from "../Form/GdprCheckbox.svelte";
	import EmailMarketingCheckbox from "../Form/EmailMarketingCheckbox.svelte";
	import RecaptchaDisclaimer from "../Form/RecaptchaDisclaimer.svelte";
	import Button from "../Button.svelte";
	import Modal from "../Modal.svelte";
	import Form from "../Form/Form.svelte";
	import Message from "../Message.svelte";
	import { mutation } from "../../api/graphql.js";
	import translations from "./CareerForm.translations.json";
	import arrowRight from "../../../../core/assets/icons/arrow-right.svg?raw";
	import FileUploader from "../Form/FileUploader.svelte";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import type { Attachment } from "../../../../core/schema/Attachment.js";
	import type { Position } from "../../../../core/schema/Position.js";
	import { ContactFormType } from "../../../../core/schema/ContactFormType.js";
	import { createEventDispatcher } from "svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { getTenant } from "../../getTenant.js";
	import { getTranslate } from "../../utils/getTranslate.js";

	export let position: Position | undefined = undefined;

	const translate = getTranslate(translations);
	const dispatch = createEventDispatcher<{
		// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
		close: void;
	}>();

	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();

	const tenant = getTenant();

	const send = createPromiseStore(async (input: ContactFormInput) => {
		await mutation<boolean>(PUBLIC_GRAPHQL_ENDPOINT, sendContactForm, { input });
	});

	let uploadedFile: UploadedFile | undefined;
	let showSuccessModal = false;
	let finished = false;
	async function submit(event: CustomEvent<SubmitEvent>): Promise<void> {
		const form = event.detail.target as HTMLFormElement;
		const formData = new FormData(form);

		const attachment: Attachment | undefined = uploadedFile && {
			...uploadedFile,
			title: uploadedFile.title ?? "CV",
		};
		const emailMarketingApprovedAt = formData.get("emailMarketingApprovedAt") as string;

		const input: ContactFormInput = {
			email: (formData.get("email") as string).trim(),
			...(emailMarketingApprovedAt && { emailMarketingApprovedAt }),
			file: attachment,
			gdprApprovedAt: formData.get("gdprApprovedAt") as string,
			message: (formData.get("message") as string).trim(),
			name: (formData.get("name") as string).trim(),
			pageUrl: window.location.href,
			phoneNumber: (formData.get("phoneNumber") as string).trim(),
			position: position?.id,
			locale: tenant.locale,
			type: ContactFormType.Career,
		};
		await send.call(input);
		showSuccessModal = true;
		finished = true;
	}

	let form: HTMLFormElement | null = null;
	$: {
		if (finished && !showSuccessModal) {
			form?.reset();
			uploadedFile = undefined;
			dispatch("close");
		}
	}
</script>

<Modal bind:show={showSuccessModal}>
	<p class="h2" slot="title">{translate("thanks")}</p>
	<div class="flex flex-col items-center gap-2">
		<p>{translate("willBeInTouch")}</p>

		<Button
			on:click={() => {
				showSuccessModal = false;
			}}
		>
			{translate("understand")}
		</Button>
	</div>
</Modal>

<div class="flex flex-col">
	<div class="border-lttertiary border-b px-8 pb-8">
		<p class="font-lexend !mb-0 max-w-lg !text-base font-normal leading-[1.6] tracking-normal text-black">
			{translate("desc")}
		</p>
	</div>

	<Form bind:form id="career-form" on:submit={submit}>
		<div class="grid w-full items-end">
			<LabelledInput
				class="border-lttertiary border-b [&_.input]:!px-8 [&_.label]:!left-8"
				id="career-name"
				name="name"
				required
				autocomplete="given-name">{translate("name")}</LabelledInput
			>
			<LabelledInput
				class="border-lttertiary border-b [&_.input]:!px-8 [&_.label]:!left-8"
				id="career-phoneNumber"
				name="phoneNumber"
				required
				type="tel"
				autocomplete="tel"
				inputProps={{ pattern: "[+]?[\\d ]+" }}
			>
				{translate("phone")}
			</LabelledInput>
			<LabelledInput
				class="border-lttertiary border-b [&_.input]:!px-8 [&_.label]:!left-8"
				id="career-email"
				name="email"
				required
				type="email"
				autocomplete="email">{translate("email")}</LabelledInput
			>
		</div>
		<div class="relative w-full">
			<textarea
				id="career-message"
				name="message"
				class="input-with-label border-lttertiary peer h-40 max-h-80 w-full resize-y border-b px-8 !pb-4 !pt-8 text-xs focus:font-semibold focus:outline-none"
				placeholder=" "
				required
				minlength="10"
			></textarea>
			<label class="label textarea-label !left-8" for="career-message">{translate("yourMessage")}</label>
		</div>

		<div class="border-lttertiary border-b p-8">
			<FileUploader
				destinationFolder="cv"
				currentFiles={uploadedFile ? [uploadedFile] : []}
				on:change={({ detail: { files } }) => {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
					uploadedFile = files;
				}}
				required
				white
				border={false}
			>
				<span
					class="font-inter mx-auto block max-w-96 text-center text-xs/[1.4] font-normal italic tracking-normal text-[#333333]"
				>
					{translate("uploadHint")}
				</span>
			</FileUploader>
		</div>

		<div class="flex flex-col gap-6 p-8">
			<div class="flex flex-col gap-2">
				<GdprCheckbox class="!mt-0 justify-start" />
				<EmailMarketingCheckbox class="!mt-0 justify-start" />
			</div>

			<Button
				class="mx-[initial] w-fit"
				icon={arrowRight}
				type="submit"
				disabled={$send.status === "loading"}
				variant="primary"
			>
				{#if $send.status === "loading"}
					{translate("submitting")}…
				{:else}
					{translate("submit")}
				{/if}
			</Button>

			{#if $send.status === "error"}
				<Message variant="error">{translate("somethingWrong")}</Message>
			{/if}

			<RecaptchaDisclaimer />
		</div>
	</Form>
</div>
